import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import { makeRequest, axiosOrgInstance, type ApiEnvelope } from '@/stores/middleware';
import lmsRouter from '@/router/lms-router';
import adminRouter from '@/router/admin-router';
import router from '@/router';
import { useRoute } from 'vue-router';
import type { SimpleCourse } from './store-interfaces';
import { getCPRolesFromToken, isVisitor } from './helpers';
import { getHardcodedCourse } from '@/content/lms-course';

export const useLMSStore = defineStore('lms', () => {
    const currentRouter = computed(() =>
        localStorage.getItem('lms') ? lmsRouter : localStorage.getItem('admin') === 'true' ? adminRouter : router,
    );
    const pendingCourses = ref<SimpleCourse[]>([]);
    const isLMS = computed(() => JSON.parse(localStorage.getItem('lms') ?? 'false'));
    const isAuthenticated = computed(() => localStorage.getItem('isAuthenticated') === 'true');
    const activeSectionIndex = computed(() => localStorage.getItem('activeSectionIndex') ?? '0');
    const completedSectionLastIndex = computed(() => localStorage.getItem('completedSectionLastIndex') ?? '0');
    const courseStartTime = computed(() => localStorage.getItem('courseStartTime') ?? '0');
    if (
        isAuthenticated.value &&
        localStorage.getItem('tokenExpires') !== null &&
        new Date(Number(localStorage.getItem('tokenExpires')) * 1000).getTime() > Date.now()
    ) {
        void setup();
    }

    async function setup() {
        const userRoles = getCPRolesFromToken(localStorage.getItem('token') ?? '');
        pendingCourses.value = await getPendingCourses();
        const route = useRoute();

        if (
            pendingCourses.value?.length > 0 &&
            isLMS.value &&
            route &&
            !['about', 'course', 'login'].some((path) => route.fullPath.includes(path))
        ) {
            lmsRouter.replace({
                name: 'about',
            });
        } else if (pendingCourses.value?.length > 0 && !isLMS.value) {
            handleLMSSwitch();
        } else if (pendingCourses.value?.length > 0 && isLMS.value) {
            return;
        } else if (isVisitor(userRoles)) {
            localStorage.setItem('lms', 'true');
            lmsRouter.replace({ name: 'completed', params: { courseId: 'Visitor' } });
            return;
        }
    }

    // ITDEVELOP-3118: LMS course content is intentionally bundled in portal-vue because the legacy LMS is being
    // deprecated. Keep using the backend-provided courseId: assignment lookup and course completion still depend on
    // it to preserve user progress.
    function getCourse(courseId: string) {
        return getHardcodedCourse(courseId);
    }

    async function getCourses() {
        const response = await makeRequest<ApiEnvelope<SimpleCourse[]>>(
            'GET',
            `lms-management/courses`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function getPendingCourses() {
        const response = await makeRequest<ApiEnvelope<SimpleCourse[]>>(
            'GET',
            `lms-management/courses?status=unstarted`,
            axiosOrgInstance,
        );
        return response.data?.data;
    }

    // also used in header, refactor there if also refactored here. Tricky to setup as a helper because of our use of different vue routers
    async function handleLMSSwitch() {
        console.log('handleLMSSwitch');
        if (!isLMS.value) {
            localStorage.setItem('lms', 'true');
            lmsRouter
                .replace({
                    name: 'about',
                })
                .then(() => {
                    setTimeout(() => {
                        window.location.reload();
                    }, 500);
                });
        } else {
            localStorage.setItem('lms', 'false');
            currentRouter.value.replace('/').then(() => {
                window.location.reload();
            });
        }
    }

    async function completeCourse(courseId: string) {
        const now = Math.floor(Date.now() / 1000);
        await makeRequest('PUT', `lms-management/courses/${courseId}/completion`, axiosOrgInstance, {
            start_time: courseStartTime.value,
            end_time: now,
        }).then(async () => {
            localStorage.removeItem('courseStartTime');
            localStorage.removeItem('activeSectionIndex');
            localStorage.removeItem('completedSectionLastIndex');
            pendingCourses.value = await getPendingCourses();
        });
    }

    function setActiveSectionIndex(index: string) {
        localStorage.setItem('activeSectionIndex', index);
    }

    function setCompletedSectionLastIndex(index: string) {
        localStorage.setItem('completedSectionLastIndex', index);
    }

    function setCourseStartTime() {
        localStorage.setItem('courseStartTime', Math.floor(Date.now() / 1000).toString());
    }

    return {
        //constants
        pendingCourses,
        isLMS,
        activeSectionIndex,
        completedSectionLastIndex,
        //functions
        setup,
        getCourses,
        getCourse,
        getPendingCourses,
        handleLMSSwitch,
        setActiveSectionIndex,
        setCompletedSectionLastIndex,
        completeCourse,
        setCourseStartTime,
    };
});
